/**
 * NotificationPreferences — Per-event-type channel + severity settings
 * Phase 4.7.5 (WQ-170)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listEventTypes,
  getSubscriptions,
  updateSubscription,
  type EventType,
  type SubscriptionEntry,
} from '../../services/notificationService';

const CHANNELS = ['in_app', 'email', 'webhook'] as const;
const CATEGORY_ORDER = ['approval', 'review', 'alert', 'informational'];

const NotificationPreferences: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionEntry[]>([]);
  const [defaults, setDefaults] = useState<Array<{ event_type_slug: string; channel: string; enabled: boolean; min_severity: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [types, subs] = await Promise.all([
        listEventTypes(authFetch),
        getSubscriptions(authFetch),
      ]);
      setEventTypes(types.filter((t) => t.is_active));
      setSubscriptions(subs.subscriptions);
      setDefaults(subs.defaults);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, fetchData]);

  const getSubForChannel = (slug: string, channel: string): { enabled: boolean; min_severity: string } => {
    const sub = subscriptions.find((s) => s.event_type_slug === slug && s.channel === channel);
    if (sub) return { enabled: sub.enabled, min_severity: sub.min_severity };
    const def = defaults.find((d) => (d.event_type_slug === slug || d.event_type_slug === '**') && d.channel === channel);
    if (def) return { enabled: def.enabled, min_severity: def.min_severity };
    return { enabled: false, min_severity: 'low' };
  };

  const handleToggle = async (slug: string, channel: string, currentEnabled: boolean) => {
    const key = `${slug}-${channel}`;
    setSaving(key);
    try {
      const result = await updateSubscription(authFetch, {
        event_type_slug: slug,
        channel,
        enabled: !currentEnabled,
      });
      setSubscriptions((prev) => {
        const idx = prev.findIndex((s) => s.event_type_slug === slug && s.channel === channel);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = result;
          return next;
        }
        return [...prev, result];
      });
    } catch { /* silent */ }
    finally { setSaving(null); }
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    types: eventTypes.filter((t) => t.category === cat),
  })).filter((g) => g.types.length > 0);

  if (loading) return <div style={{ padding: '20px', color: 'var(--text-muted)' }}>Loading preferences...</div>;

  return (
    <div>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)', color: 'var(--text-primary)' }}>
        Notification Preferences
      </h3>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: 'var(--space-4)' }}>
        Configure which events generate notifications and via which channels.
      </p>

      {grouped.map((group) => (
        <div key={group.category} style={{ marginBottom: 'var(--space-6)' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'capitalize', marginBottom: 'var(--space-3)' }}>
            {group.category}
          </h4>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <th style={thStyle}>Event</th>
                  {CHANNELS.map((ch) => (
                    <th key={ch} style={{ ...thStyle, textAlign: 'center', width: '80px' }}>
                      {ch === 'in_app' ? 'In-App' : ch === 'email' ? 'Email' : 'Webhook'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.types.map((et) => (
                  <tr key={et.slug} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                        {et.slug.replace(/\./g, ' > ').replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{et.producer_service}</div>
                    </td>
                    {CHANNELS.map((ch) => {
                      const state = getSubForChannel(et.slug, ch);
                      const key = `${et.slug}-${ch}`;
                      return (
                        <td key={ch} style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={state.enabled}
                            disabled={saving === key}
                            onChange={() => handleToggle(et.slug, ch, state.enabled)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: '12px',
  color: 'var(--text-muted)',
  fontWeight: 600,
};

export default NotificationPreferences;
